import React, { useEffect, useState } from 'react';
import { reportsAPI } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge } from '@/components/ui';
import { LoadingSpinner } from '@/components/common';
import { BarChart3, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useBranchScope } from '@/context/BranchScopeContext';
import { toast } from 'react-toastify';
import { downloadBlob } from '@/utils/download';

type ReportType = 'daily' | 'monthly' | 'payroll';

const ATTENDANCE_STATUS_META: Record<string, { label: string; variant: string }> = {
  present: { label: 'Присутствовал', variant: 'success' },
  late: { label: 'Опоздал', variant: 'warning' },
  absent: { label: 'Отсутствовал', variant: 'destructive' },
  no_show: { label: 'Неявка', variant: 'destructive' },
  holiday: { label: 'Праздник', variant: 'secondary' },
  weekend: { label: 'Выходной', variant: 'secondary' },
  leave: { label: 'Отпуск', variant: 'secondary' },
  unpaid_leave: { label: 'Без оплаты', variant: 'secondary' },
  day_off: { label: 'Отгул', variant: 'secondary' },
};

const PENALTY_TYPE_LABELS: Record<string, string> = {
  late: 'Опоздание',
  early_leave: 'Ранний уход',
  absent: 'Отсутствие',
  no_show: 'Неявка',
  no_checkout: 'Нет отметки ухода',
  manual: 'Ручной штраф',
};

const getAttendanceMeta = (status?: string) =>
  ATTENDANCE_STATUS_META[status || ''] || { label: status || '-', variant: 'secondary' };

const getPenaltyTypeLabel = (type?: string) => PENALTY_TYPE_LABELS[type || ''] || type || '-';

export default function ReportsPage() {
  const { can } = useAuth();
  const { branchParam } = useBranchScope();
  const canViewAttendance = can('view_attendance');
  const canViewPayroll = can('view_payroll');
  const [reportType, setReportType] = useState<ReportType>(canViewAttendance ? 'daily' : 'payroll');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  const canAccessReportType = (type: ReportType) =>
    type === 'payroll' ? canViewPayroll : canViewAttendance;

  useEffect(() => {
    if (canAccessReportType(reportType)) {
      return;
    }

    if (canViewAttendance) {
      setReportType('daily');
      setReportData(null);
      return;
    }

    if (canViewPayroll) {
      setReportType('payroll');
      setReportData(null);
    }
  }, [canViewAttendance, canViewPayroll, reportType]);

  const handleTypeChange = (nextType: ReportType) => {
    setReportType(nextType);
    setReportData(null);
  };

  const loadReport = async () => {
    if (!canAccessReportType(reportType)) {
      toast.error('У вас нет прав на просмотр этого отчета');
      return;
    }

    setLoading(true);
    try {
      const data = reportType === 'daily'
        ? await reportsAPI.daily({ date, branch_id: branchParam })
        : reportType === 'monthly'
          ? await reportsAPI.monthly({ month, branch_id: branchParam })
          : await reportsAPI.payroll({ month, branch_id: branchParam });

      setReportData(data);
    } catch (error: any) {
      setReportData(null);
      toast.error(error?.message || 'Не удалось загрузить отчет');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!canAccessReportType(reportType)) {
      toast.error('У вас нет прав на экспорт этого отчета');
      return;
    }

    try {
      const blob = await reportsAPI.export({
        type: reportType,
        month,
        date,
        branch_id: branchParam,
      });

      downloadBlob(blob, `report-${reportType}-${reportType === 'daily' ? date : month}.xlsx`);
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка при экспорте');
    }
  };

  if (!canViewAttendance && !canViewPayroll) {
    return <div className="py-12 text-center text-gray-500">Недостаточно прав для просмотра этой страницы.</div>;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Отчеты</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} disabled={loading || !canAccessReportType(reportType)}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Экспорт в Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {canViewAttendance && (
          <>
            <button
              onClick={() => handleTypeChange('daily')}
              className={`w-full rounded-xl px-4 py-3 text-left font-medium transition ${
                reportType === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ежедневный отчет
            </button>
            <button
              onClick={() => handleTypeChange('monthly')}
              className={`w-full rounded-xl px-4 py-3 text-left font-medium transition ${
                reportType === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ежемесячный отчет
            </button>
          </>
        )}
        {canViewPayroll && (
          <button
            onClick={() => handleTypeChange('payroll')}
            className={`w-full rounded-xl px-4 py-3 text-left font-medium transition ${
              reportType === 'payroll' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Месячный срез
          </button>
        )}
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            {reportType === 'daily' ? (
              <div className="flex-1">
                <label className="mb-1 block text-sm text-gray-500">Дата</label>
                <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
              </div>
            ) : (
              <div className="flex-1">
                <label className="mb-1 block text-sm text-gray-500">Месяц</label>
                <Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
              </div>
            )}
            <Button onClick={loadReport} disabled={loading} className="md:w-auto">
              {loading ? 'Загрузка...' : 'Сформировать отчет'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingSpinner />
      ) : reportData ? (
        <ReportContent type={reportType} data={reportData} />
      ) : (
        <div className="py-12 text-center">
          <BarChart3 className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Выберите тип отчета</h3>
          <p className="text-gray-500">Укажите период и сформируйте отчет.</p>
        </div>
      )}
    </div>
  );
}

function ReportContent({ type, data }: { type: string; data: any }) {
  const report = data?.data || data;
  const summary = report?.summary || {};

  if (type === 'daily') {
    const employees = report.employees || report.attendance_details || [];

    return (
      <Card>
        <CardHeader>
          <CardTitle>Ежедневный отчет по посещаемости</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg bg-green-50 p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{summary.present ?? report.present ?? 0}</p>
              <p className="text-sm text-gray-500">Присутствовали</p>
            </div>
            <div className="rounded-lg bg-orange-50 p-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{summary.late ?? report.late ?? 0}</p>
              <p className="text-sm text-gray-500">Опоздали</p>
            </div>
            <div className="rounded-lg bg-red-50 p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{summary.absent ?? report.absent ?? 0}</p>
              <p className="text-sm text-gray-500">Отсутствовали</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{Number(summary.penalties_amount ?? 0).toLocaleString('ru-RU')}</p>
              <p className="text-sm text-gray-500">Сумма штрафов за день</p>
            </div>
          </div>

          {employees.length > 0 && (
            <div className="overflow-x-auto ui-table-wrap">
              <table className="w-full ui-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Сотрудник</th>
                    <th className="px-4 py-2 text-left">Статус</th>
                    <th className="px-4 py-2 text-left">Смена</th>
                    <th className="px-4 py-2 text-left">Вход</th>
                    <th className="px-4 py-2 text-left">Выход</th>
                    <th className="px-4 py-2 text-left">Часы</th>
                    <th className="px-4 py-2 text-left">Штрафы</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee: any, index: number) => {
                    const statusMeta = getAttendanceMeta(employee.status);

                    return (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">{employee.name || employee.employee_name}</td>
                        <td className="px-4 py-2">
                          <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
                        </td>
                        <td className="px-4 py-2">{employee.shift_name || '-'}</td>
                        <td className="px-4 py-2">{employee.check_in || employee.first_in || '-'}</td>
                        <td className="px-4 py-2">{employee.check_out || employee.last_out || '-'}</td>
                        <td className="px-4 py-2">{employee.hours || '-'}</td>
                        <td className="px-4 py-2 text-red-600">{Number(employee.penalties_amount || 0).toLocaleString('ru-RU')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (type === 'payroll') {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg bg-blue-50 p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{summary.total_employees || 0}</p>
            <p className="text-sm text-gray-500">Сотрудники</p>
          </div>
          <div className="rounded-lg bg-red-50 p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{Number(summary.approved_penalties_amount || 0).toLocaleString('ru-RU')}</p>
            <p className="text-sm text-gray-500">Подтвержденные штрафы</p>
          </div>
          <div className="rounded-lg bg-yellow-50 p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{Number(summary.pending_penalties_amount || 0).toLocaleString('ru-RU')}</p>
            <p className="text-sm text-gray-500">Ожидающие штрафы</p>
          </div>
          <div className="rounded-lg bg-green-50 p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{summary.total_overtime_hours || 0}</p>
            <p className="text-sm text-gray-500">Сверхурочно, часов</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Месячный срез по сотрудникам</CardTitle>
          </CardHeader>
          <CardContent>
            {report.employees ? (
              <div className="overflow-x-auto ui-table-wrap">
                <table className="w-full ui-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Сотрудник</th>
                      <th className="px-4 py-2 text-left">Филиал</th>
                      <th className="px-4 py-2 text-left">Отработано дней</th>
                      <th className="px-4 py-2 text-left">Часы</th>
                      <th className="px-4 py-2 text-left">Сверхурочно</th>
                      <th className="px-4 py-2 text-left">Подтверждено</th>
                      <th className="px-4 py-2 text-left">Ожидает</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.employees.map((employee: any, index: number) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">{employee.employee_name || employee.name}</td>
                        <td className="px-4 py-2">{employee.branch || '-'}</td>
                        <td className="px-4 py-2">{employee.days_worked ?? employee.worked_days}</td>
                        <td className="px-4 py-2">{employee.total_hours}</td>
                        <td className="px-4 py-2 text-green-600">+{employee.overtime_hours || 0}</td>
                        <td className="px-4 py-2 text-red-600">{Number(employee.penalty_amount || employee.approved_penalties_amount || 0).toLocaleString('ru-RU')}</td>
                        <td className="px-4 py-2 text-yellow-600">{Number(employee.pending_penalties_amount || 0).toLocaleString('ru-RU')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">Данные отсутствуют</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const monthlyEmployees = report.employees || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{summary.total_worked_days || 0}</p>
          <p className="text-sm text-gray-500">Рабочие дни</p>
        </div>
        <div className="rounded-lg bg-red-50 p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{summary.total_absent_days || 0}</p>
          <p className="text-sm text-gray-500">Отсутствия</p>
        </div>
        <div className="rounded-lg bg-orange-50 p-4 text-center">
          <p className="text-3xl font-bold text-orange-600">{summary.total_late_count || 0}</p>
          <p className="text-sm text-gray-500">Опоздания</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{Number(summary.total_penalties || 0).toLocaleString('ru-RU')}</p>
          <p className="text-sm text-gray-500">Общая сумма штрафов</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Штрафы по типам</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Тип штрафа</th>
                  <th className="px-4 py-2 text-left">Количество</th>
                  <th className="px-4 py-2 text-left">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report.penalty_breakdown || {}).map(([type, value]: [string, any]) => (
                  <tr key={type} className="border-t">
                    <td className="px-4 py-2">{getPenaltyTypeLabel(type)}</td>
                    <td className="px-4 py-2">{value.count}</td>
                    <td className="px-4 py-2 text-red-600">{Number(value.amount || 0).toLocaleString('ru-RU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {monthlyEmployees.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Сотрудники за месяц</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto ui-table-wrap">
              <table className="w-full ui-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Сотрудник</th>
                    <th className="px-4 py-2 text-left">Филиал</th>
                    <th className="px-4 py-2 text-left">Отработано</th>
                    <th className="px-4 py-2 text-left">Отсутствия</th>
                    <th className="px-4 py-2 text-left">Опоздания</th>
                    <th className="px-4 py-2 text-left">Штрафов</th>
                    <th className="px-4 py-2 text-left">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyEmployees.map((employee: any, index: number) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2">{employee.employee_name}</td>
                      <td className="px-4 py-2">{employee.branch || '-'}</td>
                      <td className="px-4 py-2">{employee.worked_days}</td>
                      <td className="px-4 py-2">{employee.absent_days + employee.no_show_days}</td>
                      <td className="px-4 py-2">{employee.late_days}</td>
                      <td className="px-4 py-2">{employee.penalties_count}</td>
                      <td className="px-4 py-2 text-red-600">{Number(employee.penalties_amount || 0).toLocaleString('ru-RU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


